import {DeleteOutlined, UploadOutlined} from '@ant-design/icons';
import {Button, Flex, Upload, message} from 'antd';
import type {UploadProps} from 'antd';
import React, {useState} from 'react';

import uploadAttachedDocumentAPI from '../../../services/modules/UploadAndJobs/uploadAttachedDocumentAPI';
import {
  ATTACHED_DOCUMENT_ACCEPT,
  ATTACHED_DOCUMENT_MAX_MB,
  ATTACHED_DOCUMENT_MIME_TYPES
} from '../Constants/IP/attachedDocumentConstants';

import {parseAttachedDocumentDisplayName} from './attachedDocumentUtils';

interface AttachedDocumentUploadProps {
  value?: string;
  onChange?: (url?: string) => void;
  uploadEndpoint: string;
}

const AttachedDocumentUpload: React.FC<AttachedDocumentUploadProps> = ({
  value,
  onChange,
  uploadEndpoint
}) => {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadAttachedDocumentAPI({
        endpoint: uploadEndpoint,
        deletePath: value ?? undefined,
        file
      });

      if (result.success && result.location) {
        onChange?.(result.location);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!value) {
      onChange?.(undefined);

      return;
    }

    setRemoving(true);
    try {
      const result = await uploadAttachedDocumentAPI({
        endpoint: uploadEndpoint,
        deletePath: value
      });

      if (result.success) {
        onChange?.(undefined);
      }
    } finally {
      setRemoving(false);
    }
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isValidType = ATTACHED_DOCUMENT_MIME_TYPES.includes(file.type);

    if (!isValidType) {
      void message.error('Only PDF, JPG, and PNG files are allowed');

      return Upload.LIST_IGNORE;
    }

    const isLtMax = file.size / 1024 / 1024 < ATTACHED_DOCUMENT_MAX_MB;
    if (!isLtMax) {
      void message.error(
        `File must be smaller than ${ATTACHED_DOCUMENT_MAX_MB}MB`
      );

      return Upload.LIST_IGNORE;
    }

    void handleUpload(file as File);

    return false;
  };

  const uploadProps: UploadProps = {
    accept: ATTACHED_DOCUMENT_ACCEPT,
    showUploadList: false,
    beforeUpload,
    disabled: uploading || removing
  };

  const displayName = value ? parseAttachedDocumentDisplayName(value) : '';

  return (
    <Flex vertical gap={8}>
      {value ? (
        <Flex align="center" gap={12} wrap="wrap">
          <a href={value} target="_blank" rel="noreferrer">
            {displayName}
          </a>
          <Upload {...uploadProps}>
            <Button
              icon={<UploadOutlined />}
              loading={uploading}
              disabled={removing}
            >
              Replace
            </Button>
          </Upload>
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={removing}
            disabled={uploading}
            onClick={() => {
              void handleRemove();
            }}
          >
            Remove
          </Button>
        </Flex>
      ) : (
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />} loading={uploading}>
            Upload document
          </Button>
        </Upload>
      )}
    </Flex>
  );
};

export default AttachedDocumentUpload;
